"use client";

import {
  formatChartDetailDisplayValue,
  formatChartDisplayValue,
  svgChartScrubIndexForX,
  type T_SvgChartScrubGeometry,
} from "@lattice/shared-frontend/charts";
import React, { useCallback, useMemo, useRef, useState } from "react";

import type {
  T_ChartData,
  T_ExpectedActualChartPresentation,
} from "@lattice/core-lib/types/chart";

import { ChartSeriesHeader } from "./ChartSeriesHeader";
import { getPointValue } from "./chart-data";
import "./charts-kit.css";

const VIEWBOX_WIDTH = 300;
const VIEWBOX_HEIGHT = 190;
const X_START = 8;
const X_END = 292;
const Y_START = 12;
const Y_END = 178;

export type T_ExpectedActualChartProps = {
  chart: T_ChartData;
  presentation: T_ExpectedActualChartPresentation;
};

type T_ExpectedActualGeometry = {
  grid: number[];
  leftLabels: string[];
  points: Array<{
    actual: number | null;
    actualFill: string;
    actualY: number | null;
    connectorY1: number;
    connectorY2: number;
    cx: number;
    expected: number;
    expectedY: number;
    label: string;
  }>;
};

/**
 * Mirror of the mobile buildExpectedActualGeometry — same viewbox, same
 * padding rules, so both platforms plot identically.
 */
function buildExpectedActualGeometry(
  chart: T_ChartData,
  presentation: T_ExpectedActualChartPresentation,
): T_ExpectedActualGeometry {
  const rows = [
    ...chart.data.flatMap((point) => {
      const labelEntry = Object.entries(point).find(([key]) => key === "label");
      const expected = getPointValue(point, presentation.expectedSeries);
      const actual = getPointValue(point, presentation.actualSeries);
      if (typeof labelEntry?.[1] !== "string" || expected === null) return [];
      return [{ actual, expected, label: labelEntry[1] }];
    }),
    ...(presentation.pendingExpectedPoints ?? []).map((point) => ({
      actual: null,
      expected: point.value,
      label: point.label,
    })),
  ];
  const values = rows
    .flatMap((row) => [row.expected, row.actual])
    .filter((value): value is number => value !== null)
    .concat([0]);
  let minimum = Math.min(...values);
  let maximum = Math.max(...values);
  const span = maximum - minimum;
  const padding = (span || Math.max(Math.abs(maximum), 1)) * 0.12;
  maximum += padding;
  if (minimum < 0) minimum -= padding;
  const valueToY = (value: number) =>
    Y_END - ((value - minimum) / (maximum - minimum || 1)) * (Y_END - Y_START);
  const slot = (X_END - X_START) / Math.max(rows.length, 1);
  const format = (value: number) =>
    formatChartDisplayValue(value, {
      valueFormat: chart.valueFormat,
      valuePrefix: chart.valuePrefix,
      valueSuffix: chart.valueSuffix,
    });

  return {
    grid: [Y_START, (Y_START + Y_END) / 2, Y_END],
    leftLabels: [maximum, (maximum + minimum) / 2, minimum].map(format),
    points: rows.map((row, index) => {
      const cx = X_START + slot * (index + 0.5);
      const expectedY = valueToY(row.expected);
      const actualY = row.actual === null ? null : valueToY(row.actual);
      return {
        actual: row.actual,
        actualFill:
          row.actual !== null && row.actual >= row.expected
            ? "var(--lattice-financial-positive)"
            : "var(--lattice-financial-negative)",
        actualY,
        connectorY1: expectedY,
        connectorY2: actualY ?? expectedY,
        cx,
        expected: row.expected,
        expectedY,
        label: row.label,
      };
    }),
  };
}

/**
 * Expected-versus-actual presentation — web mirror of the mobile
 * ExpectedActualChart. Hand-rolled SVG (recharts has no paired-dot +
 * connector grammar); pointer scan maps x → slot index with the SAME shared
 * svg-chart-scrub-math both platforms use, keyed by index per policy.
 */
export function ExpectedActualChart({
  chart,
  presentation,
}: T_ExpectedActualChartProps) {
  const geometry = useMemo(
    () => buildExpectedActualGeometry(chart, presentation),
    [chart, presentation],
  );
  const plotRef = useRef<HTMLDivElement | null>(null);
  const [scanIndex, setScanIndex] = useState<number | null>(null);
  const scrubGeometry = useMemo<T_SvgChartScrubGeometry>(
    () => ({
      slotCount: geometry.points.length,
      viewboxWidth: VIEWBOX_WIDTH,
      xEnd: X_END,
      xStart: X_START,
    }),
    [geometry.points.length],
  );

  const scanFromClientX = useCallback(
    (clientX: number) => {
      const plot = plotRef.current;
      if (!plot) return;
      const rect = plot.getBoundingClientRect();
      setScanIndex(
        svgChartScrubIndexForX(clientX - rect.left, rect.width, scrubGeometry),
      );
    },
    [scrubGeometry],
  );
  const clearScan = useCallback(() => setScanIndex(null), []);

  const format = (value: number) =>
    formatChartDetailDisplayValue(value, {
      valueFormat: chart.valueFormat,
      valuePrefix: chart.valuePrefix,
      valueSuffix: chart.valueSuffix,
    });

  // Header shows the scanned point while scanning, otherwise the latest.
  const headerIndex = scanIndex ?? geometry.points.length - 1;
  const headerPoint = geometry.points[headerIndex];

  if (geometry.points.length === 0) {
    return (
      <div className="ck-root">
        <div className="ck-empty">No chart data</div>
      </div>
    );
  }

  return (
    <div className="ck-root">
      <div className="ck-title-row">
        <h3 className="ck-title">{chart.title ?? "Expected vs actual"}</h3>
      </div>

      {headerPoint ? (
        <ChartSeriesHeader
          entries={[
            {
              hollow: true,
              name: presentation.expectedSeries,
              value: format(headerPoint.expected),
            },
            {
              color: headerPoint.actualFill,
              name: presentation.actualSeries,
              value:
                headerPoint.actual === null
                  ? "Pending"
                  : format(headerPoint.actual),
            },
          ]}
          label={headerPoint.label}
        />
      ) : null}

      <div style={{ display: "flex", gap: 8 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            justifyContent: "space-between",
            paddingBottom: 20,
          }}
        >
          {geometry.leftLabels.map((label) => (
            <span
              key={label}
              style={{
                color: "var(--lattice-chart-axis-label)",
                fontSize: "var(--lattice-font-size-xs)",
                fontWeight: 600,
                textAlign: "right",
              }}
            >
              {label}
            </span>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="ck-plot ck-plot--scan"
            onMouseLeave={clearScan}
            onMouseMove={(event) => scanFromClientX(event.clientX)}
            onTouchEnd={clearScan}
            onTouchMove={(event) => {
              const touch = event.touches[0];
              if (touch) scanFromClientX(touch.clientX);
            }}
            onTouchStart={(event) => {
              const touch = event.touches[0];
              if (touch) scanFromClientX(touch.clientX);
            }}
            ref={plotRef}
          >
            <svg
              className="ck-svg-plot"
              preserveAspectRatio="none"
              viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
              width="100%"
            >
              {geometry.grid.map((gridY) => (
                <line
                  key={gridY}
                  stroke="var(--lattice-chart-grid)"
                  strokeWidth={1}
                  x1={0}
                  x2={VIEWBOX_WIDTH}
                  y1={gridY}
                  y2={gridY}
                />
              ))}
              {scanIndex !== null && headerPoint ? (
                <line
                  stroke="var(--lattice-chart-zero-line)"
                  strokeWidth={1.4}
                  x1={headerPoint.cx}
                  x2={headerPoint.cx}
                  y1={Y_START}
                  y2={Y_END}
                />
              ) : null}
              {geometry.points.map((point, index) => (
                <React.Fragment key={`${point.label}-${index}`}>
                  <line
                    stroke="var(--lattice-chatkit-eps-connector)"
                    strokeWidth={1}
                    x1={point.cx}
                    x2={point.cx}
                    y1={point.connectorY1}
                    y2={point.connectorY2}
                  />
                  <circle
                    cx={point.cx}
                    cy={point.expectedY}
                    fill="var(--lattice-chart-dot-core)"
                    r={index === scanIndex ? 7 : 6}
                    stroke="var(--lattice-chatkit-eyebrow)"
                    strokeWidth={2}
                  />
                  {point.actualY !== null ? (
                    <circle
                      cx={point.cx}
                      cy={point.actualY}
                      fill={point.actualFill}
                      r={index === scanIndex ? 7.4 : 6.4}
                    />
                  ) : null}
                </React.Fragment>
              ))}
            </svg>
          </div>
          <div style={{ display: "flex", marginTop: 8 }}>
            {geometry.points.map((point, index) => (
              <span
                key={`${point.label}-${index}`}
                style={{
                  color:
                    index === scanIndex
                      ? "var(--lattice-palette-white-pure)"
                      : "var(--lattice-chart-axis-label)",
                  flex: 1,
                  fontSize: "var(--lattice-font-size-xs)",
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                {point.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
